import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { formatMoney, formatDate } from '../core/format.js';
import { MANUAL_PAYMENT_METHODS, paymentMethodLabel } from '../core/paymentMethods.js';
import { showModal } from '../components/Modal.js';
import { toast } from '../components/Toast.js';

export class DashboardView extends Component {
  constructor() {
    super();
    this.state = { data: null, feed: null, error: null, kind: null };
  }

  template() {
    const data = this.state.data;
    const isLandlord = this.state.kind === 'landlord';
    if (this.state.error) return `<section class="stack"><div class="error-banner">${esc(this.state.error)}</div></section>`;
    if (data === null)    return `<section class="stack"><div class="muted">${esc(t('common.loading'))}</div></section>`;
    if (isLandlord)       return this._landlordHtml(data);
    return this._tenantHtml(data);
  }

  _landlordHtml(d) {
    const unread = Number(d.unread_notifications) || 0;
    return `
      <section class="stack">
        <div class="panel">
          <div class="panel-head">
            <div class="panel-titles">
              <div class="panel-title-eyebrow">${esc(t('dashboard.eyebrow_landlord'))}</div>
              <h2 class="panel-title">${esc(t('dashboard.landlord_title'))}</h2>
            </div>
            <div class="panel-status">
              <span class="badge primary"><span class="dot-anim"></span>${esc(t('dashboard.live'))}</span>
            </div>
          </div>
          <div class="panel-body">
            <p class="panel-lede">${esc(t('dashboard.landlord_lede'))}</p>
            <div class="stats">
              ${this._statHtml(t('dashboard.properties_total'), d.properties_total ?? '—')}
              ${this._statHtml(t('dashboard.units_total'),      d.units_total ?? '—')}
              ${this._occupancyStatHtml(d)}
              ${this._statHtml(t('dashboard.active_tenancies'), d.active_tenancies ?? '—')}
              ${this._statHtml(t('dashboard.open_inquiries'),   d.open_inquiries ?? 0, 'acc')}
              ${this._statHtml(t('dashboard.notifications_unread'), unread, unread > 0 ? 'alert' : '')}
            </div>
            <div class="dash-cols" style="margin-top:24px">
              <div>
                <div class="eyebrow" style="margin-bottom:14px">${esc(t('dashboard.action_required'))}</div>
                ${this._actionItemsHtml(d)}
                <div class="row" style="gap:var(--space-2); margin-top:18px; flex-wrap:wrap">
                  <a class="btn btn-primary" href="#/properties/new">${esc(t('dashboard.new_property'))}</a>
                </div>
              </div>
              <div>
                <div class="eyebrow" style="margin-bottom:14px">${esc(t('dashboard.recent_activity'))}</div>
                ${this._feedHtml()}
              </div>
            </div>
          </div>
        </div>
        ${this._opsHtml(d)}
        ${this._landlordRentHtml(d.rent)}
      </section>
    `;
  }

  // Occupancy = units under an ACTIVE tenancy / all units. Rendered only
  // when the backend sends occupied_units (older payloads simply skip it).
  _occupancyStatHtml(d) {
    if (d.occupied_units === undefined || d.occupied_units === null) return '';
    const total = Number(d.units_total) || 0;
    const occupied = Number(d.occupied_units) || 0;
    const value = total > 0 ? `${Math.round((occupied / total) * 100)}%` : '—';
    return `
      <div class="stat" data-stat="occupancy">
        <div class="stat-k">${esc(t('dashboard.occupancy'))}</div>
        <div class="stat-v">${esc(value)}</div>
        ${total > 0 ? `<div class="stat-trend">${esc(t('dashboard.occupancy_detail', { occupied, total }))}</div>` : ''}
      </div>
    `;
  }

  // Operations panel: expiring agreements + open maintenance, side by side.
  // Hidden entirely when the backend payload predates these fields.
  _opsHtml(d) {
    const expiring = d.expiring_tenancies;
    const maint = d.maintenance;
    if (expiring === undefined && maint === undefined) return '';
    const attention = (expiring ? expiring.length : 0) + (maint ? Number(maint.open_total) || 0 : 0);
    const status = attention > 0
      ? `<span class="badge warning">${esc(String(attention))}</span>`
      : `<span class="badge success">${esc(t('dashboard.ops.all_clear'))}</span>`;
    return `
      <div class="panel" data-panel="ops">
        <div class="panel-head">
          <div class="panel-titles">
            <div class="panel-title-eyebrow">${esc(t('dashboard.ops.eyebrow'))}</div>
            <h2 class="panel-title">${esc(t('dashboard.ops.title'))}</h2>
          </div>
          <div class="panel-status">${status}</div>
        </div>
        <div class="panel-body">
          <div class="dash-cols">
            <div>
              <div class="eyebrow" style="margin-bottom:14px">${esc(t('dashboard.ops.expiring_title'))}</div>
              ${this._expiringHtml(expiring || [])}
            </div>
            <div>
              <div class="eyebrow" style="margin-bottom:14px">${esc(t('dashboard.ops.maintenance_title'))}</div>
              ${this._maintenanceHtml(maint || { open_total: 0, requests: [] })}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _expiringHtml(rows) {
    if (rows.length === 0) return `<div class="muted">${esc(t('dashboard.ops.expiring_empty'))}</div>`;
    return rows.map((r) => this._expiringRow(r)).join('');
  }

  _expiringRow(r) {
    const days = this._daysUntil(r.end_date);
    let badge;
    if (days === null) badge = '';
    else if (days < 0) badge = `<span class="badge danger">${esc(t('dashboard.ops.expired'))}</span>`;
    else {
      const tone = days <= 14 ? 'danger' : 'warning';
      const key = days === 1 ? 'dashboard.ops.days_left_one' : 'dashboard.ops.days_left_other';
      badge = `<span class="badge ${tone}">${esc(t(key, { count: days }))}</span>`;
    }
    const where = r.property_name ? `${r.property_name} · ` : '';
    return `
      <a class="opt" href="#/tenancies/${esc(r.tenancy_id)}?action=amend" data-expiring="${esc(r.tenancy_id)}">
        <span class="opt-tick" aria-hidden="true"></span>
        <div>
          <div class="opt-t">${esc(r.tenant_name || t('tenancy.tenant_fallback'))}</div>
          <div class="opt-s">${esc(where)}${esc(formatDate(r.end_date))}</div>
        </div>
        ${badge}
      </a>
    `;
  }

  _maintenanceHtml(maint) {
    const rows = maint.requests || [];
    if (rows.length === 0) return `<div class="muted">${esc(t('dashboard.ops.maintenance_empty'))}</div>`;
    const extra = (Number(maint.open_total) || 0) - rows.length;
    return rows.map((r) => this._maintenanceRow(r)).join('')
      + (extra > 0 ? `<div class="faint" style="margin-top:8px">${esc(t('dashboard.ops.maintenance_more', { count: extra }))}</div>` : '');
  }

  _maintenanceRow(r) {
    const tone = (r.priority === 'URGENT' || r.priority === 'HIGH') ? 'danger'
      : (r.priority === 'NORMAL' ? 'warning' : 'primary');
    const where = r.property_name ? `${r.property_name} · ` : '';
    return `
      <a class="opt" href="#/tenancies/${esc(r.tenancy_id)}?tab=maintenance" data-maintenance="${esc(r.id)}">
        <span class="opt-tick" aria-hidden="true"></span>
        <div>
          <div class="opt-t">${esc(r.title)}</div>
          <div class="opt-s">${esc(where)}${esc(formatDate(r.created_at))}</div>
        </div>
        <span class="badge ${tone}">${esc(t('dashboard.ops.priority.' + r.priority))}</span>
      </a>
    `;
  }

  _daysUntil(dateStr) {
    if (!dateStr) return null;
    const end = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(end.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((end - today) / 86400000);
  }

  // Portfolio rent snapshot: four money stats + a worst-first tenancy ledger.
  _landlordRentHtml(rent) {
    if (!rent) return '';
    const cur = rent.currency || 'HUF';
    const overdueCount = Number(rent.overdue_tenancies) || 0;
    const status = overdueCount > 0
      ? `<span class="badge danger">${esc(t(overdueCount === 1
          ? 'dashboard.rent.overdue_tenancies_one'
          : 'dashboard.rent.overdue_tenancies_other', { count: overdueCount }))}</span>`
      : `<span class="badge success">${esc(t('dashboard.rent.all_clear'))}</span>`;
    return `
      <div class="panel">
        <div class="panel-head">
          <div class="panel-titles">
            <div class="panel-title-eyebrow">${esc(t('dashboard.rent.eyebrow'))}</div>
            <h2 class="panel-title">${esc(t('dashboard.rent.title'))}</h2>
          </div>
          <div class="panel-status">${status}</div>
        </div>
        <div class="panel-body">
          <div class="stats">
            ${this._moneyStatHtml(t('dashboard.rent.expected'), rent.expected_this_month, cur)}
            ${this._moneyStatHtml(t('dashboard.rent.collected'), rent.collected_this_month, cur, 'acc')}
            ${this._moneyStatHtml(t('dashboard.rent.outstanding'), rent.outstanding_total, cur)}
            ${this._moneyStatHtml(t('dashboard.rent.overdue'), rent.overdue_total, cur, Number(rent.overdue_total) > 0 ? 'alert' : '')}
          </div>
          <div style="margin-top:22px">
            ${this._rentLedgerHtml(rent.tenancies)}
          </div>
        </div>
      </div>
    `;
  }

  _rentLedgerHtml(rows) {
    if (!rows || rows.length === 0) {
      return `<div class="muted">${esc(t('dashboard.rent.no_tenancies_landlord'))}</div>`;
    }
    return `<div class="rent-ledger">${rows.map((r) => this._rentLedgerRow(r)).join('')}</div>`;
  }

  _rentLedgerRow(r) {
    const cur = r.currency || 'HUF';
    const name = r.tenant_name || '—';
    const where = r.property_name ? ` · ${r.property_name}` : '';
    const balance = formatMoney(r.outstanding, cur);
    const tone = r.status === 'OVERDUE' ? 'danger' : (r.status === 'DUE' ? 'warning' : 'success');
    const sub = r.status === 'CLEAR'
      ? esc(t('dashboard.rent.status.CLEAR'))
      : `${esc(t('dashboard.rent.next_due'))}: ${esc(formatDate(r.next_due_date))}`;
    const canPay = Number(r.outstanding) > 0;
    return `
      <div class="rent-row">
        <a class="rent-row-link" href="#/tenancies/${esc(r.tenancy_id)}">
          <div class="rent-row-main">
            <div class="rent-row-t">${esc(name)}<span class="faint">${esc(where)}</span></div>
            <div class="rent-row-s">${sub}</div>
          </div>
        </a>
        <div class="rent-row-amt">
          <strong>${esc(balance)}</strong>
          <span class="badge ${tone}">${esc(t('dashboard.rent.status.' + r.status))}</span>
          ${canPay ? `<button type="button" class="btn btn-ghost btn-sm rent-pay-btn"
            data-mark-paid="${esc(r.tenancy_id)}"
            data-amount="${esc(String(r.outstanding))}"
            data-currency="${esc(cur)}"
            data-name="${esc(name)}">${esc(t('dashboard.rent.mark_paid'))}</button>` : ''}
          <button type="button" class="btn btn-ghost btn-sm rent-hist-btn"
            data-history="${esc(r.tenancy_id)}"
            data-name="${esc(name)}">${esc(t('dashboard.rent.history'))}</button>
        </div>
      </div>
    `;
  }

  _moneyStatHtml(label, minor, currency, variant = '') {
    const cls = variant ? `stat ${variant}` : 'stat';
    return `
      <div class="${cls}">
        <div class="stat-k">${esc(label)}</div>
        <div class="stat-v">${esc(formatMoney(minor, currency))}</div>
      </div>
    `;
  }

  _actionItemsHtml(d) {
    const items = [];
    const inq = Number(d.open_inquiries) || 0;
    if (inq > 0) {
      const key = inq === 1 ? 'dashboard.action.inquiries_s_one' : 'dashboard.action.inquiries_s_other';
      items.push(this._optHtml('#/inquiries', t('dashboard.action.inquiries_t'), t(key, { count: inq }), inq, 'primary'));
    }
    const unread = Number(d.unread_notifications) || 0;
    if (unread > 0) {
      const key = unread === 1 ? 'dashboard.action.notifications_s_one' : 'dashboard.action.notifications_s_other';
      items.push(this._optHtml('#/notifications', t('dashboard.action.notifications_t'), t(key, { count: unread }), unread, 'danger'));
    }
    if (items.length === 0) return `<div class="muted">${esc(t('dashboard.no_action'))}</div>`;
    return items.join('');
  }

  _optHtml(href, title, subtitle, count, tone) {
    return `
      <a class="opt" href="${esc(href)}">
        <span class="opt-tick" aria-hidden="true"></span>
        <div>
          <div class="opt-t">${esc(title)}</div>
          <div class="opt-s">${esc(subtitle)}</div>
        </div>
        <span class="badge ${esc(tone)}">${esc(String(count))}</span>
      </a>
    `;
  }

  _feedHtml() {
    const feed = this.state.feed;
    if (feed === null) return `<div class="muted">${esc(t('common.loading'))}</div>`;
    if (feed.length === 0) return `<div class="muted">${esc(t('dashboard.no_recent_activity'))}</div>`;
    return feed.slice(0, 5).map((n) => this._feedRow(n)).join('');
  }

  _feedRow(n) {
    const time = this._shortTime(n.created_at);
    const title = n.title || '';
    const body = n.body || '';
    return `
      <a class="feed-i" href="#/notifications">
        <span class="t">${esc(time)}</span>
        <div><b>${esc(title)}</b>${body ? ' — ' + esc(body) : ''}</div>
        ${n.read_at ? '' : `<span class="badge primary">${esc(t('dashboard.notifications_unread'))}</span>`}
      </a>
    `;
  }

  _shortTime(value) {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  _tenantHtml(d) {
    const rent = d.rent || null;
    const hasTenancy = rent && Array.isArray(rent.tenancies) && rent.tenancies.length > 0;
    const cur = (rent && rent.currency) || 'HUF';
    const balance = rent ? Number(rent.outstanding_total) || 0 : 0;
    const overdue = rent ? Number(rent.overdue_total) || 0 : 0;
    const nextDue = hasTenancy ? rent.tenancies[0].next_due_date : null;

    const balanceBlock = hasTenancy ? `
      <div class="rent-hero ${balance > 0 ? (overdue > 0 ? 'is-overdue' : 'is-due') : 'is-clear'}">
        <div class="rent-hero-k">${esc(t('dashboard.rent.balance'))}</div>
        <div class="rent-hero-v">${esc(formatMoney(balance, cur))}</div>
        ${balance === 0
          ? `<div class="rent-hero-note">${esc(t('dashboard.rent.balance_clear'))}</div>`
          : (nextDue ? `<div class="rent-hero-note">${esc(t('dashboard.rent.next_due'))}: ${esc(formatDate(nextDue))}</div>` : '')}
        ${overdue > 0 ? `<div class="rent-hero-warn">${esc(t('dashboard.rent.overdue_warning_one', { amount: formatMoney(overdue, cur) }))}</div>` : ''}
      </div>
    ` : `<p class="panel-lede">${esc(t('dashboard.rent.no_tenancies_tenant'))}</p>`;

    return `
      <section class="stack">
        <div class="panel">
          <div class="panel-head">
            <div class="panel-titles">
              <div class="panel-title-eyebrow">${esc(t('dashboard.rent.tenant_eyebrow'))}</div>
              <h2 class="panel-title">${esc(t('dashboard.rent.tenant_title'))}</h2>
            </div>
          </div>
          <div class="panel-body stack">
            ${balanceBlock}
            ${hasTenancy ? `
              <div>
                <div class="eyebrow" style="margin-bottom:14px">${esc(t('dashboard.rent.recent_payments'))}</div>
                ${this._tenantPaymentsHtml(rent.recent_payments)}
              </div>
              <div class="row" style="gap:var(--space-2); flex-wrap:wrap">
                <a class="btn btn-ghost" href="#/tenancies/${esc(rent.tenancies[0].tenancy_id)}">${icon('receipt', { size: 'sm' })}<span>${esc(t('dashboard.rent.view_details'))}</span></a>
              </div>
            ` : ''}
            <div class="row" style="gap:var(--space-2); flex-wrap:wrap">
              <a class="btn btn-ghost" href="#/tenant-requests/incoming">${icon('inbox', { size: 'sm' })}<span>${esc(t('tenant_requests.inbox.title'))}</span></a>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  _tenantPaymentsHtml(payments) {
    if (!payments || payments.length === 0) {
      return `<div class="muted">${esc(t('dashboard.rent.no_payments'))}</div>`;
    }
    return payments.map((p) => `
      <div class="feed-i">
        <span class="t">${esc(formatDate(p.paid_at))}</span>
        <div><b>${esc(formatMoney(p.amount, p.currency))}</b>${p.property_name ? ' — ' + esc(p.property_name) : ''}</div>
      </div>
    `).join('');
  }

  _statHtml(label, value, variant = '') {
    const cls = variant ? `stat ${variant}` : 'stat';
    return `
      <div class="${cls}">
        <div class="stat-k">${esc(label)}</div>
        <div class="stat-v">${esc(String(value))}</div>
      </div>
    `;
  }

  mount() {
    if (this.state.data === null) { this._load(); return; }
    this.el.querySelectorAll('[data-mark-paid]').forEach((btn) => {
      btn.addEventListener('click', () => this._openMarkPaid({
        tenancyId: btn.getAttribute('data-mark-paid'),
        amount: Number(btn.getAttribute('data-amount')) || 0,
        currency: btn.getAttribute('data-currency') || 'HUF',
        name: btn.getAttribute('data-name') || '',
      }));
    });
    this.el.querySelectorAll('[data-history]').forEach((btn) => {
      btn.addEventListener('click', () => this._openHistory({
        tenancyId: btn.getAttribute('data-history'),
        name: btn.getAttribute('data-name') || '',
      }));
    });
  }

  // Per-tenancy payment-history drill-down: open a modal, then fetch the
  // tenancy's payments (the same endpoint the tenancy detail uses) and render
  // them newest-first. Complements "mark paid" — record, then see it land here.
  async _openHistory({ tenancyId, name }) {
    const body = document.createElement('div');
    body.className = 'stack';
    body.innerHTML = `<div class="muted">${esc(t('common.loading'))}</div>`;
    showModal({
      title: t('dashboard.rent.history_title'),
      body,
      actions: [{ label: t('common.close'), variant: 'ghost' }],
    });
    try {
      const res = await api.get(`/tenancies/${tenancyId}/payments`);
      body.innerHTML = this._historyBodyHtml(res.data || [], name);
    } catch (e) {
      body.innerHTML = `<div class="error-banner">${esc(e instanceof ApiError ? e.message : t('dashboard.rent.history_error'))}</div>`;
    }
  }

  _historyBodyHtml(payments, name) {
    const header = name ? `<p class="muted" style="margin:0 0 4px">${esc(name)}</p>` : '';
    if (payments.length === 0) {
      return `${header}<div class="muted">${esc(t('dashboard.rent.history_empty'))}</div>`;
    }
    const rows = payments.map((p) => {
      const method = paymentMethodLabel(p.method);
      return `
        <div class="feed-i">
          <span class="t">${esc(formatDate(p.paid_at))}</span>
          <div><b>${esc(formatMoney(p.amount, p.currency))}</b>${method ? ' — ' + esc(method) : ''}</div>
        </div>
      `;
    }).join('');
    return `${header}${rows}`;
  }

  // Inline "mark paid": prefill the outstanding balance, let the landlord
  // adjust amount/method, post to the existing payments endpoint, refresh.
  _openMarkPaid({ tenancyId, amount, currency, name }) {
    const major = (amount / 100).toFixed(currency === 'HUF' ? 0 : 2);
    const methods = MANUAL_PAYMENT_METHODS;
    const body = document.createElement('form');
    body.className = 'stack';
    body.innerHTML = `
      ${name ? `<p class="muted" style="margin:0">${esc(name)}</p>` : ''}
      <label class="stack-2">
        <span>${esc(t('dashboard.rent.amount_label'))} (${esc(currency)})</span>
        <input class="input" type="number" min="0" step="1" name="amount" value="${esc(major)}" required />
      </label>
      <label class="stack-2">
        <span>${esc(t('dashboard.rent.method_label'))}</span>
        <select class="input" name="method">
          ${methods.map((m) => `<option value="${m}">${esc(t('dashboard.rent.method.' + m))}</option>`).join('')}
        </select>
      </label>
    `;
    showModal({
      title: t('dashboard.rent.modal_title'),
      body,
      actions: [
        { label: t('common.cancel'), variant: 'ghost' },
        {
          label: t('dashboard.rent.record'),
          variant: 'primary',
          onClick: async () => {
            const value = parseFloat(body.querySelector('[name="amount"]').value);
            const minor = Math.round(value * 100);
            const method = body.querySelector('[name="method"]').value;
            if (!Number.isFinite(minor) || minor <= 0) {
              toast.error(t('dashboard.rent.amount_invalid'));
              return false; // keep the modal open
            }
            try {
              await api.post(`/tenancies/${tenancyId}/payments`, { amount: minor, method });
              toast.success(t('dashboard.rent.payment_recorded'));
              this._load(); // re-fetch so balances/overdue update in place
            } catch (e) {
              toast.error(e instanceof ApiError ? e.message : t('dashboard.rent.record_error'));
              return false;
            }
          },
        },
      ],
    });
  }

  async _load() {
    const isLandlord = auth.canManageProperties;
    const path = isLandlord ? '/dashboard/landlord' : '/dashboard/tenant';
    try {
      const data = await api.get(path);
      this.setState({ data, kind: isLandlord ? 'landlord' : 'tenant' });
      if (isLandlord) this._loadFeed();
    } catch (e) {
      this.setState({ error: e instanceof ApiError ? e.message : t('common.failed_to_load') });
    }
  }

  async _loadFeed() {
    try {
      const res = await api.get('/notifications');
      this.setState({ feed: res.data || [] });
    } catch (_) {
      // The feed is best-effort; the main view still renders without it.
      this.setState({ feed: [] });
    }
  }
}
